/**
 * The Credit Note / Sales Return editor — draft edit only.
 *
 * WHY IT IS SMALLER THAN THE INVOICE EDITOR
 * ----------------------------------------
 * A return is not a new sale. It corrects one, so every field that could differ
 * from the invoice — the customer, the address, the rate, the tax slab, the unit
 * — is SNAPSHOTTED from the invoice rather than re-entered. Re-typing a rate is
 * how a credit note ends up reversing a different amount of tax than the invoice
 * bore, and then the customer's credit does not match their own bill.
 *
 * So the only things the user chooses are WHICH LINES and HOW MANY, plus the date
 * and a reason. Everything else is the invoice's, and the screen says so.
 *
 * CREATION lives on the invoice screen, not here. A return always begins from a
 * specific invoice, so the picker is the invoice list the user is already looking
 * at — a free-standing "new return" form would have to ask for the invoice anyway
 * while looking like a second way to sell something.
 */
import { useMemo, useState } from 'react';
import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { DocumentScreen } from '@features/documents/DocumentScreen';
import { toast } from '@ui/components/Toaster';
import { returnTypeLabel, type ReturnDocType } from '@data/services/salesReturnService';
import { useReturnDocument, useReturnSource, useUpdateReturn } from './useSalesReturns';

const inputClass =
  'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100';

const today = () => new Date().toISOString().slice(0, 10);

const BASE: Record<ReturnDocType, string> = {
  CREDIT_NOTE: '#/sales/credit-notes',
  SALES_RETURN: '#/sales/returns',
};

interface LineState {
  readonly sourceLineId: string;
  readonly quantity: string;
  /** What this draft may still take back, in milli-units. */
  readonly available: number;
  readonly unit: string;
  readonly description: string;
  readonly rate: string;
}

/** The subset of a returnable invoice line this screen draws. */
interface SourceLine {
  readonly id: string;
  readonly returnableQtyMilli: number;
  readonly description: string;
  readonly unit: string;
  readonly ratePaise: number;
}

export function ReturnEditorScreen({
  docType,
  documentId = null,
}: {
  readonly docType: ReturnDocType;
  readonly documentId?: string | null;
}) {
  const editing = documentId !== null;
  const label = returnTypeLabel(docType);
  const existing = useReturnDocument(documentId);
  const invoiceId = existing.data?.stored.originalDocId ?? null;
  const source = useReturnSource(invoiceId, editing ? (documentId ?? undefined) : undefined);
  const update = useUpdateReturn();

  const [docDate, setDocDate] = useState(today);
  const [lines, setLines] = useState<readonly LineState[]>([]);
  const [reason, setReason] = useState('');
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * SEEDING DURING RENDER, GUARDED — the pattern the Delivery Note editor uses,
   * and for the same two reasons: an effect re-runs on every refetch and would
   * overwrite what the user had just typed, and `setState` inside an effect body
   * causes a cascading render. The guard seeds exactly once per document.
   */
  if (editing && documentId && existing.data && loadedId !== documentId) {
    if (existing.data.stored.status === 'DRAFT') {
      const stored = existing.data;
      const savedQty = new Map(
        stored.lines
          .map(
            (line): readonly [string, number] => [
              stored.sourceLineByReturnLineId.get(line.id) ?? '',
              line.quantityMilli,
            ],
          )
          .filter(([id]) => id !== ''),
      );
      const base: readonly SourceLine[] =
        source.data && source.data.lines.length
          ? source.data.lines
          : stored.lines.map((line) => ({
              id: stored.sourceLineByReturnLineId.get(line.id) ?? '',
              returnableQtyMilli: line.quantityMilli,
              description: line.description,
              unit: line.unit,
              ratePaise: line.ratePaise,
            }));
      setLines(
        base.map((line) => {
          const own = savedQty.get(line.id) ?? 0;
          return {
            sourceLineId: line.id,
            quantity: String(own / 1000),
            // The source read EXCLUDES this draft's own links, so its own
            // quantity is already inside what remains; nothing to add back.
            available: Number(line.returnableQtyMilli) + own,
            unit: line.unit,
            description: line.description,
            rate: String(line.ratePaise / 100),
          };
        }),
      );
      setDocDate(stored.stored.docDate);
      setReason(stored.stored.notes?.replace(/^Reason: /, '') ?? '');
      setLoadedId(documentId);
    }
  }

  const chosen = useMemo(() => lines.filter((line) => Number(line.quantity) > 0), [lines]);
  const overQuantity = chosen.some(
    (line) => Math.round((Number(line.quantity) || 0) * 1000) > line.available,
  );
  /** The estimated credit value, shown live so the effect is visible before issue. */
  const estimatePaise = chosen.reduce(
    (sum, line) =>
      sum + Math.round((Number(line.quantity) || 0) * 100) * (Number(line.rate) || 0),
    0,
  );

  async function save() {
    if (!docDate || chosen.length === 0 || !documentId) {
      toast({ title: 'Choose at least one line to credit or return', tone: 'danger' });
      return;
    }
    setBusy(true);
    try {
      await update.mutateAsync({
        id: documentId,
        input: {
          docDate,
          reason: reason.trim() || undefined,
          lines: chosen.map((line) => ({
            sourceLineId: line.sourceLineId,
            quantityMilli: Math.round((Number(line.quantity) || 0) * 1000),
          })),
        },
      });
      toast({
        title: `${label} ${existing.data?.stored.docNumber} saved as draft`,
        tone: 'success',
      });
    } catch (error) {
      toast({
        title: `Could not save the ${label.toLowerCase()}`,
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  if (editing && existing.isLoading) {
    return <p className="p-4 text-sm text-slate-400">Loading {label.toLowerCase()}…</p>;
  }
  // An ISSUED credit note or return is immutable. Its own screen is where the
  // printed copy lives, and this one has no business editing it.
  if (editing && existing.data && existing.data.stored.status !== 'DRAFT') {
    return <DocumentScreen documentId={documentId as string} />;
  }

  return (
    <div className="flex flex-col gap-4 p-4 text-slate-100">
      <header>
        <h1 className="text-lg font-semibold">Edit {label.toLowerCase()} draft</h1>
        <p className="text-xs text-slate-400">
          Rates, tax and the customer come from invoice{' '}
          {existing.data?.invoice?.docNumber ?? ''}. Only the quantities are yours to choose.
        </p>
      </header>

      <section className="flex flex-col gap-3 rounded border border-slate-800 p-4">
        <label className="block text-xs text-slate-400">
          Date
          <input
            data-testid="return-date"
            type="date"
            className={`${inputClass} mt-1`}
            value={docDate}
            onChange={(event) => setDocDate(event.target.value)}
          />
        </label>

        {lines.length === 0 ? (
          <p className="text-sm text-slate-400">This return has no lines.</p>
        ) : (
          <table className="w-full text-sm">
            <caption className="sr-only">Quantities to credit or return</caption>
            <thead>
              <tr className="text-left text-xs uppercase text-slate-400">
                <th scope="col" className="p-2">Item</th>
                <th scope="col" className="p-2">Rate</th>
                <th scope="col" className="p-2">Available</th>
                <th scope="col" className="p-2">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.sourceLineId} data-testid="return-line">
                  <td className="p-2">
                    {line.description}
                    <span className="ml-1 text-xs text-slate-500">{line.unit}</span>
                  </td>
                  <td className="p-2 text-slate-400">Rs {line.rate}</td>
                  <td className="p-2 text-slate-400">{line.available / 1000}</td>
                  <td className="p-2">
                    <input
                      data-testid="return-quantity"
                      aria-label={`Quantity to credit or return for ${line.description}`}
                      className={`${inputClass} w-24`}
                      inputMode="decimal"
                      value={line.quantity}
                      onChange={(event) =>
                        setLines((old) =>
                          old.map((item) =>
                            item.sourceLineId === line.sourceLineId
                              ? { ...item, quantity: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <label className="block text-xs text-slate-400">
          Reason
          <input
            data-testid="return-reason"
            className={`${inputClass} mt-1`}
            placeholder="Damaged in transit, wrong item, customer changed their mind…"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>

        {overQuantity && (
          <p role="alert" className="text-xs text-amber-300">
            A quantity exceeds what is still returnable on this invoice.
          </p>
        )}
      </section>

      <aside className="h-fit rounded border border-slate-800 p-4">
        <h2 className="mb-3 text-xs uppercase text-slate-400">Summary</h2>
        <div className="flex justify-between border-t border-slate-700 py-2">
          <span>Estimated value</span>
          <span data-testid="return-estimate">{formatMoney(asMoney(estimatePaise))}</span>
        </div>
        <button
          type="button"
          data-testid="return-save"
          disabled={busy || update.isPending || chosen.length === 0}
          onClick={() => void save()}
          className="mt-3 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium disabled:opacity-40"
        >
          Save changes
        </button>
        {documentId && (
          <a
            className="mt-2 block rounded border border-slate-700 px-3 py-2 text-center text-sm"
            href={`${BASE[docType]}/${documentId}`}
          >
            Preview without saving
          </a>
        )}
        <a className="mt-2 block text-center text-sm text-slate-400" href={BASE[docType]}>
          Back to {label.toLowerCase()}s
        </a>
      </aside>
    </div>
  );
}